import { useCallback, useContext, useEffect, useRef } from 'react';
import { RemotionPlayerContext } from '../context/RemotionPlayerContext';
import {
  setCurrentTime,
  setCurrentTimeWithoutSeeking
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';

export const useRemotionPlayerSeek = () => {
  const { playerRef } = useContext(RemotionPlayerContext);

  const { fps } = useClipsContext();

  const seekListenersRef = useRef<Set<(time: number) => void>>(new Set());

  const subscribeToSeekEvents = useCallback((listener: (time: number) => void) => {
    seekListenersRef.current.add(listener);

    return () => {
      seekListenersRef.current.delete(listener);
    };
  }, []);

  const notifySeekListeners = useCallback((time: number) => {
    seekListenersRef.current.forEach(listener => listener(time));
  }, []);

  useEffect(() => {
    if (playerRef) {
      const handleSeek = ({ detail: { frame } }: { detail: { frame: number } }) => {
        notifySeekListeners(frame / fps);
      };

      playerRef.addEventListener('seeked', handleSeek);
      return () => {
        playerRef.removeEventListener('seeked', handleSeek);
      };
    }
  }, [notifySeekListeners, playerRef, fps]);

  const seekTo = useCallback(
    (duration: number, play?: boolean) => {
      if (!playerRef) {
        setCurrentTimeWithoutSeeking(duration);
      } else {
        setCurrentTime(duration, fps, playerRef);
        if (play === true) {
          playerRef.play();
        } else if (play === false) {
          playerRef.pause();
        }
      }
    },
    [playerRef, fps]
  );

  const seekToFrame = useCallback(
    (frame: number) => {
      if (!playerRef) {
        return null;
      }
      playerRef.seekTo(frame);
    },
    [playerRef]
  );

  return { subscribeToSeekEvents, seekTo, seekToFrame };
};
